/*
  Handles authentication permissions and redirects
  Example:
  <Authenticator requires="admin">
    <h1>Only admins will see this header</h1>
  </Authenticator>

  <Authenticator requires="editor" redirect="/401">
    <h1>If not editor will be redirected to 401</h1>
  </Authenticator>

  <Authenticator requires="admin" subweb="deca">
    <h1>Only admins of the subweb 'deca' or of the editor will see this</h1>
  </Authenticator>
*/

import React, { useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useUserContext } from 'context/UserContext';
import { useRouterContext } from 'hooks';

const roles = {
  ADMIN: 'admin',
  EDITOR: 'editor',
  USER: 'user'
}

const rolesArr = Object.values(roles)

export const Authenticator = ({
  requires, redirect, children, subweb
}) => {
  const [{ user, userAuthenticated }] = useUserContext();
  const { history } = useRouterContext();

  // to check if user is an admin for a specific subweb
  const hasSubwebAdminPermissions = React.useMemo(() => {
    if (!subweb || !user || !user.subwebs || !user.subwebs.length > 0) return false
    const subwebData = user.subwebs.find(
      subwebData => subwebData.slug === subweb || encodeURIComponent(subwebData.slug) === subweb
    )
    return (subwebData && subwebData.admin) ? subwebData.admin : false
  }, [subweb, user])

  // computed
  const hasAdminPermissions = useMemo(() => user && !!user.admin, [user])
  const hasEditorPermissions = useMemo(() => user && !!user.editor, [user])
  const hasUserPermissions = useMemo(() => user && !!user.user, [user])
  const hasPermissions = useMemo(() => {
    if (requires === roles.ADMIN) return hasSubwebAdminPermissions || hasAdminPermissions
    if (requires === roles.EDITOR) return hasEditorPermissions
    if (requires === roles.USER) return hasUserPermissions
    return userAuthenticated
  }, [
    requires,
    hasAdminPermissions,
    hasSubwebAdminPermissions,
    hasEditorPermissions,
    hasUserPermissions,
    userAuthenticated
  ])

  // handle redirect
  useEffect(() => {
    if (userAuthenticated && (!hasPermissions && redirect)) {
      history.push(redirect)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermissions, redirect, userAuthenticated])

  return hasPermissions ? (
    <>
      {children}
    </>
  ) : null
}

Authenticator.propTypes = {
  requires: PropTypes.oneOf(rolesArr),
  redirect: PropTypes.string,
  children: PropTypes.node,
  subweb: PropTypes.string,
}

Authenticator.defaultProps = {
  requires: roles.USER,
  redirect: null,
  children: null,
  subweb: null,
}

export default Authenticator
